/* eslint-disable react-hooks/exhaustive-deps */

import { FileStore } from "#/api/fileStore.type";
import {
  AdditionalUploadData,
  MedicalRecordIngestResult,
  UploadAsset,
  UploadMultipleFilesResponse,
} from "#/api/storage.type";
import { UseMutateFunction, useMutation } from "@tanstack/react-query";
import {
  ChangeEvent,
  createContext,
  useContext,
  useRef,
  useState,
} from "react";
import { uploadFile } from "../api/storage.api";
import { useToast } from "./ToastContext";

export const FileUploadContextWrapper = (props: any) => {
  const [files, setFiles] = useState<File[]>([]);
  const hiddenFileInput = useRef<HTMLInputElement>(null);

  const [fileName, setFileName] = useState("");
  const { showToast } = useToast();

  const handleOpenFileSelect = (e: any) => {
    e.preventDefault();
    hiddenFileInput?.current?.click();
  };

  const handleMultipleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setFiles(Array.from(event.target.files));
    }
  };

  const handleRemoveImage = (index: number) => {
    setFiles((prevFiles) => {
      const newFiles = [...prevFiles];
      newFiles.splice(index, 1);
      return newFiles;
    });
  };

  const createUploadAssetFromFile = (file: File): UploadAsset => ({
    uri: file.name,
    fileName: file.name,
    mimeType: file.type || undefined,
    type: file.type?.toLowerCase().startsWith("image") ? "image" : undefined,
    width: undefined,
    height: undefined,
    fileSize: file.size,
    file,
  });

  const {
    mutate: uploadFilesForChat,
    data: uploadMultipleData,
    isPending,
  } = useMutation<
    UploadMultipleFilesResponse,
    Error,
    {
      files: File[];
      additionalData?: AdditionalUploadData;
    }
  >({
    mutationFn: ({ files, additionalData }) =>
      uploadFile({
        assets: files.map((file) => createUploadAssetFromFile(file)),
        additionalData,
      }),
    onSuccess() {
      showToast("Files uploaded", "success");
    },
    onError(error: Error) {
      showToast(error.message, "error");
    },
  });

  const { mutate: uploadSource, isPending: sourceFileUploading } = useMutation<
    UploadMultipleFilesResponse,
    Error,
    {
      files: File[];
      additionalData?: AdditionalUploadData;
    }
  >({
    mutationFn: ({ files, additionalData }) =>
      uploadFile({
        assets: files.map((file) => createUploadAssetFromFile(file)),
        additionalData,
      }),
    onSuccess() {
      showToast("Files uploaded", "success");
    },
    onError(error: Error) {
      showToast(error.message, "error");
    },
  });

  const fileUploadResponse = uploadMultipleData?.fileStores;
  const medicalRecordIngest = uploadMultipleData?.medicalRecordIngest;

  const value = {
    handleOpenFileSelect,
    files,
    hiddenFileInput,
    handleMultipleChange,
    uploadFilesForChat,
    uploadSource,
    fileUploadResponse,
    medicalRecordIngest,
    fileName,
    setFileName,
    setFiles,
    fileUploading: isPending || sourceFileUploading,
    handleRemoveImage,
  };

  return (
    <FileUploadContext.Provider value={value}>
      {props.children}
    </FileUploadContext.Provider>
  );
};

export const FileUploadContext = createContext({
  uploadFilesForChat: {} as UseMutateFunction<
    UploadMultipleFilesResponse,
    Error,
    {
      files: File[];
      additionalData?: AdditionalUploadData;
    },
    unknown
  >,
  uploadSource: {} as UseMutateFunction<
    UploadMultipleFilesResponse,
    Error,
    {
      files: File[];
      additionalData?: AdditionalUploadData;
    },
    unknown
  >,
  handleOpenFileSelect: (e?: any) => {},
  hiddenFileInput: null as any,
  fileUploadResponse: undefined as FileStore[] | undefined,
  medicalRecordIngest: undefined as
    | MedicalRecordIngestResult
    | MedicalRecordIngestResult[]
    | undefined,
  handleMultipleChange: (event: ChangeEvent<HTMLInputElement>) => {},
  files: [] as File[],
  fileName: "",
  setFileName: (fileName: string) => {},
  setFiles: {} as React.Dispatch<React.SetStateAction<File[]>>,
  fileUploading: false,
  handleRemoveImage: (index: number) => {},
});

export const useFileUploadContext = () => useContext(FileUploadContext);
